// src/api/Product.js
// WordPress-backed ProductsAPI (seho-rest-api plugin)
//
// ✅ CRA only
// ✅ Uses WP REST: {WP_BASE}/wp-json/seho/v1
// ✅ IMPORTANT: credentials are OMIT to avoid CORS credential rules

import { REST_BASE } from "../lib/http";

const VALID_SITES = ["b2c", "b2b", "both"];

/* -------------------------------- helpers -------------------------------- */

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function normalizeMode(mode) {
  const m = String(mode || "b2c").toLowerCase();
  return m === "b2b" ? "b2b" : "b2c";
}

function buildUrl(path, params = {}) {
  const base = `${String(REST_BASE || "").replace(/\/+$/, "")}${path}`;
  const url = new URL(base);

  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = String(v);
    if (s === "") return;
    url.searchParams.set(k, s);
  });

  return url.toString();
}

async function readJsonSafely(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isFormData(v) {
  return typeof FormData !== "undefined" && v instanceof FormData;
}

async function wpFetch(method, path, body, { params, headers } = {}) {
  const url = buildUrl(path, params);

  const h = { ...(headers || {}) };

  // If caller passes a JSON string body, add content-type automatically
  if (typeof body === "string" && !h["Content-Type"] && !h["content-type"]) {
    h["Content-Type"] = "application/json";
  }

  // For FormData: DO NOT set Content-Type manually
  if (isFormData(body)) {
    delete h["Content-Type"];
    delete h["content-type"];
  }

  const res = await fetch(url, {
    method,
    credentials: "omit", // ✅ CRITICAL
    headers: Object.keys(h).length ? h : undefined,
    body: body ?? undefined,
  });

  const data = await readJsonSafely(res);

  if (!res.ok) {
    const msg =
      (data && (data.error || data.message)) ||
      `WP products request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }

  return data;
}

function withLangParams(params = {}, lang) {
  const L = normalizeLang(lang);
  return { ...params, lang: L };
}

/* ---------- helper builders ---------- */

function toUploadImageBatchForm(files, { folder } = {}) {
  const fd = new FormData();

  // Your products.php accepts: files[] (preferred) OR file
  // We'll always use files[].
  const add = (f) => f instanceof File && fd.append("files[]", f);

  if (Array.isArray(files)) files.forEach(add);
  else if (files) add(files);

  if (folder) fd.set("folder", String(folder));

  return fd;
}

/* --------------------------------- API ---------------------------------- */

export const ProductsAPI = {
  // GET /product/all
  async list({ mode = "b2c", lang, userID } = {}) {
    const params = withLangParams(
      {
        mode: normalizeMode(mode),
        userID: userID || "",
      },
      lang,
    );
    return await wpFetch("GET", "/product/all", null, { params });
  },

  // GET /product/grouped
  async groupList({ mode = "b2c", lang, userID } = {}) {
    const params = withLangParams(
      {
        mode: normalizeMode(mode),
        userID: userID || "",
      },
      lang,
    );
    return await wpFetch("GET", "/product/grouped", null, { params });
  },

  // GET /product/{id}
  async read(productID, { lang, userID } = {}) {
    const params = withLangParams({ userID: userID || "" }, lang);
    return await wpFetch(
      "GET",
      `/product/${encodeURIComponent(productID)}`,
      null,
      { params },
    );
  },

  // GET /product/by-category?code=...&mode=b2b|b2c&userID=...
  async byCategory({ code, lang, mode, userID } = {}) {
    const params = withLangParams(
      {
        code: code || "",
        mode: normalizeMode(mode),
        userID: userID || "",
      },
      lang,
    );
    return await wpFetch("GET", "/product/by-category", null, { params });
  },

  // GET /product/by-category-tree
  async byCategoryTree({ lang, userID } = {}) {
    const params = withLangParams({ userID: userID || "" }, lang);
    return await wpFetch("GET", "/product/by-category-tree", null, { params });
  },

  // GET /product/parent/{parentID}
  async detail(parentID, { lang, userID } = {}) {
    const params = withLangParams({ userID: userID || "" }, lang);
    return await wpFetch(
      "GET",
      `/product/parent/${encodeURIComponent(parentID)}`,
      null,
      { params },
    );
  },

  // GET /product/parent/{parentID}/variants?sortA=...&sortB=...&sortC=...&userID=...
  async detailWithVariants(parentID, params = {}) {
    const qp = withLangParams(
      {
        sortA: params.sortA || undefined,
        sortB: params.sortB || undefined,
        sortC: params.sortC || undefined,
        userID: params.userID || "",
      },
      params.lang,
    );

    try {
      return await wpFetch(
        "GET",
        `/product/parent/${encodeURIComponent(parentID)}/variants`,
        null,
        { params: qp },
      );
    } catch (e) {
      // fallback to non-filtered
      return await wpFetch(
        "GET",
        `/product/parent/${encodeURIComponent(parentID)}`,
        null,
        {
          params: withLangParams({ userID: params.userID || "" }, params.lang),
        },
      );
    }
  },

  // GET /product/parent/{parentID}/variant-options
  async variantsOpt(parentID, { lang, userID } = {}) {
    const params = withLangParams({ userID: userID || "" }, lang);

    try {
      const raw = await wpFetch(
        "GET",
        `/product/parent/${encodeURIComponent(parentID)}/variant-options`,
        null,
        { params },
      );
      return {
        ...raw,
        variantA: raw?.variantA,
        variantB: raw?.variantB,
        variantC: raw?.variantC,
      };
    } catch (e) {
      return { variantA: null, variantB: null, variantC: null };
    }
  },

  // POST /product/uploadimagebatch
  async uploadImageBatch(files, { folder, lang } = {}) {
    const fd = toUploadImageBatchForm(files, { folder });
    const params = withLangParams({}, lang);
    return await wpFetch("POST", "/product/uploadimagebatch", fd, { params });
  },

  // POST /product/bulkcreatefromexcel
  async bulkCreateFromExcel(items = [], { dryRun = false, lang } = {}) {
    const params = withLangParams({}, lang);
    const body = JSON.stringify({
      dryRun: !!dryRun,
      items: Array.isArray(items) ? items : [],
    });
    return await wpFetch("POST", "/product/bulkcreatefromexcel", body, {
      params,
    });
  },

  // GET /product-groups
  async productGroups({ lang } = {}) {
    const params = withLangParams({}, lang);
    return await wpFetch("GET", "/product-groups", null, { params });
  },

  // GET /product/by-product-group?group_id=...
  async byProductGroup({ groupID, group_id, lang, userID } = {}) {
    const params = withLangParams(
      {
        group_id: groupID || group_id || "",
        userID: userID || "",
      },
      lang,
    );

    return await wpFetch("GET", "/product/by-product-group", null, { params });
  },

  // GET /product/suggestions?product_code=...
  async suggestions({ productCode, product_code, lang } = {}) {
    const params = withLangParams(
      {
        product_code: productCode || product_code || "",
      },
      lang,
    );

    return await wpFetch("GET", "/product-suggestions", null, { params });
  },
};
